import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setRetreats as setReduxRetreats,
  setRetreatsLoading,
  setRetreatsError,
  setSelectedRetreat as setReduxSelectedRetreat,
} from '../../../redux/slices/retreatSlice';
import retreatService from '../services/retreatService';
import Login from '../../../components/Login';
import '../../SharedTheme.css';

const RETREAT_FALLBACK = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80';

const getAccomFee = (r) =>
  r?.accommodation_Fees || r?.Accommodation_Fees || r?.accommodationFees || r?.accommodation_fees || 0;

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const RetreatSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');
  const country = searchParams.get('country') || 'USA';
  const fetchingRef = useRef(false);

  const { isAuthenticated } = useSelector((state) => state.auth);
  const centerData = useSelector((state) => state.center);
  const { retreats, retreatsLoading, retreatsError } = useSelector((state) => state.retreat);

  const [selectedRetreat, setSelectedRetreat] = useState(null);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    if (!centerId || fetchingRef.current) return;
    fetchRetreats();
  }, [centerId]);

  React.useEffect(() => {
    if (selectedRetreat) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [selectedRetreat]);

  const fetchRetreats = async () => {
    fetchingRef.current = true;
    try {
      dispatch(setRetreatsLoading(true));
      const data = await retreatService.getRetreatsByCenter(centerId, country);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = (data || []).filter(r => !r.camp_Date_To || new Date(r.camp_Date_To) >= today);
      dispatch(setReduxRetreats(upcoming));
    } catch (err) {
      dispatch(setRetreatsError('Failed to load retreats. Please try again.'));
      console.error(err);
    } finally {
      dispatch(setRetreatsLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleSelectRetreat = (retreat) => {
    setSelectedRetreat(retreat);
    dispatch(setReduxSelectedRetreat(retreat));
  };

  const handleProceedToRegistration = () => {
    if (!selectedRetreat) return;
    if (isAuthenticated) {
      navigate(`/retreat/registration?campId=${selectedRetreat.camp_Id}&centerId=${centerId}`);
    } else {
      setShowLogin(true);
    }
  };

  const isPastRetreat = (r) => r.camp_Date_To && new Date(r.camp_Date_To) < new Date();

  return (
    <div className="yk-page-bg min-h-screen py-8">
      <div className="container-custom">

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
                Choose Your Retreat
              </h1>
              {centerData?.center_Name && (
                <p className="text-sm text-gray-500">📍 {centerData.center_Name}</p>
              )}
            </div>
            <button
              onClick={() => navigate('/retreat/center')}
              className="yk-btn font-semibold py-2 px-6 rounded-full flex items-center gap-2"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Loading */}
        {retreatsLoading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading retreats...</p>
          </div>
        )}

        {/* Error */}
        {retreatsError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6 text-center">
            <p className="text-red-800 font-semibold mb-1">{retreatsError}</p>
            <button
              onClick={() => { dispatch(setRetreatsError(null)); fetchingRef.current = false; fetchRetreats(); }}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2 rounded-full transition text-sm mt-3"
            >
              🔄 Try Again
            </button>
          </div>
        )}

        {/* Retreat Tiles Grid */}
        {!retreatsLoading && retreats.length > 0 && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-12">
              {retreats.map((retreat, index) => {
                const past = isPastRetreat(retreat);
                const accomFee = getAccomFee(retreat);
                return (
                  <div
                    key={retreat.camp_Id}
                    onClick={() => !past && handleSelectRetreat(retreat)}
                    className={`item-card relative ${past ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${selectedRetreat?.camp_Id === retreat.camp_Id ? 'selected' : ''}`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Selected tick */}
                    {selectedRetreat?.camp_Id === retreat.camp_Id && (
                      <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    {/* Registration Closed badge */}
                    {past && (
                      <div className="absolute top-3 left-3 z-20 bg-gray-700 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                        Registration Closed
                      </div>
                    )}

                    {/* Free badge */}
                    {!past && retreat.camp_Fee === 0 && (
                      <div className="absolute top-3 left-3 z-20 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                        Free
                      </div>
                    )}

                    {/* Image */}
                    <div className="relative w-full h-52 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 flex items-center justify-center rounded-t-[18px] overflow-hidden">
                      <img
                        src={retreat.camp_Image_Url?.trim() || RETREAT_FALLBACK}
                        alt={retreat.camp_Name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => { e.target.onerror = null; e.target.src = RETREAT_FALLBACK; }}
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6 bg-gradient-to-b from-white to-gray-50/50">
                      <h3 className="text-base font-semibold text-gray-800 mb-2 line-clamp-2 leading-snug">
                        {retreat.camp_Name}
                      </h3>
                      {retreat.camp_Date_From && (
                        <div className="mb-2 text-sm text-gray-500">
                          🗓️ {fmtDate(retreat.camp_Date_From)} – {fmtDate(retreat.camp_Date_To)}
                        </div>
                      )}
                      {accomFee > 0 && (
                        <div className="mb-3 text-xs text-indigo-700 font-semibold bg-indigo-50 rounded-lg px-2 py-1 inline-block">
                          🏠 Accommodation +${accomFee}
                        </div>
                      )}
                      <button className="yk-btn w-full font-semibold py-3 px-5 rounded-full text-base">
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Side Drawer */}
            {selectedRetreat && (
              <>
                <div className="fixed inset-0 bg-black/50 z-40 animate-fadeIn" onClick={() => setSelectedRetreat(null)} />
                <div className="yk-drawer fixed top-0 right-0 h-full w-full md:w-[500px] shadow-2xl z-50 overflow-y-auto animate-slideInRight flex flex-col">

                  {/* Hero */}
                  <div className="relative w-full bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 flex-shrink-0" style={{ minHeight: '280px' }}>
                    <img
                      src={selectedRetreat.camp_Image_Url?.trim() || RETREAT_FALLBACK}
                      alt={selectedRetreat.camp_Name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      style={{ minHeight: '280px', maxHeight: '320px' }}
                      onError={(e) => { e.target.onerror = null; e.target.src = RETREAT_FALLBACK; }}
                    />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />
                    <button
                      onClick={() => setSelectedRetreat(null)}
                      className="absolute top-3 right-3 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center text-lg transition"
                    >✕</button>
                    <div className="absolute bottom-3 left-4 right-4">
                      <h2 className="text-white text-xl font-extrabold drop-shadow-lg leading-tight line-clamp-2">{selectedRetreat.camp_Name}</h2>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex-1 p-5 space-y-4">

                    {/* CTA */}
                    <button
                      onClick={handleProceedToRegistration}
                      className="yk-btn w-full font-bold py-3 rounded-2xl text-sm flex items-center justify-center gap-2"
                    >
                      🙏 Register for this Retreat
                    </button>

                    {/* Info tiles */}
                    <div className="grid grid-cols-2 gap-3">

                      {selectedRetreat.camp_Date_From && selectedRetreat.camp_Date_To && (
                        <div className="col-span-2 bg-indigo-50 border border-indigo-100 rounded-2xl px-4 py-3 flex items-center gap-3">
                          <span className="text-2xl">🗓️</span>
                          <div>
                            <p className="text-xs font-bold text-indigo-500 uppercase tracking-wider">Retreat Dates</p>
                            <p className="font-extrabold text-gray-900 text-base leading-snug">
                              {fmtDate(selectedRetreat.camp_Date_From)} – {fmtDate(selectedRetreat.camp_Date_To)}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="bg-green-50 border border-green-200 rounded-2xl px-4 py-4">
                        <p className="text-xs font-bold text-green-700 uppercase tracking-wider mb-1">🙏 Session Fee</p>
                        {selectedRetreat.camp_Fee > 0
                          ? <p className="font-black text-gray-900 text-3xl leading-none">${selectedRetreat.camp_Fee}</p>
                          : <p className="font-black text-green-700 text-3xl leading-none">Free</p>}
                        <p className="text-xs text-green-600 font-medium mt-1">per session</p>
                      </div>

                      {getAccomFee(selectedRetreat) > 0 && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-4 py-4">
                          <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">🏠 Accommodation</p>
                          <p className="font-black text-indigo-700 text-3xl leading-none">+${getAccomFee(selectedRetreat)}</p>
                          <p className="text-xs text-indigo-500 font-medium mt-1">optional on-site stay</p>
                        </div>
                      )}

                    </div>

                    {/* Contact */}
                    {(selectedRetreat.camp_PhoneNo || selectedRetreat.camp_Email || selectedRetreat.camp_Address) && (
                      <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 space-y-3">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Contact & Location</p>
                        {selectedRetreat.camp_PhoneNo && (
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center text-sm flex-shrink-0">📞</span>
                            <div>
                              <p className="text-xs text-gray-500">Phone</p>
                              <p className="font-semibold text-gray-800 text-sm">{selectedRetreat.camp_PhoneNo}</p>
                            </div>
                          </div>
                        )}
                        {selectedRetreat.camp_Email && (
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center text-sm flex-shrink-0">✉️</span>
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="font-semibold text-gray-800 text-sm break-all">{selectedRetreat.camp_Email}</p>
                            </div>
                          </div>
                        )}
                        {selectedRetreat.camp_Address && (
                          <div className="flex items-start gap-3">
                            <span className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center text-sm flex-shrink-0 mt-0.5">📍</span>
                            <div>
                              <p className="text-xs text-gray-500">Address</p>
                              <p className="font-semibold text-gray-800 text-sm">{selectedRetreat.camp_Address}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    {selectedRetreat.camp_Description && !['<P><Br></P>', '<p><br></p>'].includes(selectedRetreat.camp_Description?.trim()) && (
                      <div className="bg-white border-l-4 border-indigo-400 rounded-r-2xl p-4 shadow-sm">
                        <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-2">About this Retreat</p>
                        <div className="text-sm text-gray-800 leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: selectedRetreat.camp_Description }} />
                      </div>
                    )}

                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty State */}
        {!retreatsLoading && retreats.length === 0 && !retreatsError && (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">🧘</div>
            <p className="text-gray-600 text-lg">No retreats available for this center.</p>
          </div>
        )}

      </div>

      {showLogin && (
        <Login
          onLoginSuccess={() => {
            setShowLogin(false);
            navigate(`/retreat/registration?campId=${selectedRetreat.camp_Id}&centerId=${centerId}`);
          }}
          onCancel={() => setShowLogin(false)}
          entity="RMS"
        />
      )}
    </div>
  );
};

export default RetreatSelection;
